/**
 * OAuth2 Error Page
 *
 * This page is displayed when OAuth2 authorization fails.
 * It runs in a full page redirect (not popup).
 *
 * Flow:
 * 1. User denies authorization or error occurs in OAuth2 provider
 * 2. Provider redirects to Cloud Function oauth2Callback with error params
 * 3. Cloud Function redirects here with error details
 * 4. This page redirects back to the original page (stored in sessionStorage)
 */

import { Container, Stack, Title, Text, Alert, Button, Code } from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { TRINTA, MIL } from "@/utils/constants";

/**
 * Redirects back to the original page.
 */
function handleRedirectBack(): void {
  const flowStateStored = sessionStorage.getItem("oauth2_flow_state");
  let returnUrl = "/email-accounts"; // Default fallback

  if (flowStateStored) {
    try {
      const flowState = JSON.parse(flowStateStored) as { returnUrl?: string };
      if (flowState.returnUrl) {
        returnUrl = flowState.returnUrl;
      }
    } catch (error) {
      console.error("Failed to parse flow state:", error);
    }
  }

  window.location.href = returnUrl;
}

/**
 * Returns user-friendly error message for the given error code.
 * @param errorCode
 */
function getErrorMessage(errorCode: string): string {
  const messages: Record<string, string> = {
    access_denied: "Você negou o acesso. A autorização é necessária para conectar a conta de email.",
    invalid_request: "Requisição inválida. Verifique as configurações OAuth2 da conta.",
    unauthorized_client: "Cliente não autorizado. Verifique o Client ID nas configurações.",
    unsupported_response_type: "Tipo de resposta não suportado. Verifique as configurações OAuth2.",
    invalid_scope: "Escopo inválido. Verifique os scopes configurados.",
    server_error: "Erro no servidor OAuth2. Tente novamente mais tarde.",
    temporarily_unavailable: "Serviço temporariamente indisponível. Tente novamente mais tarde.",
    callback_failed: "Falha no callback OAuth2. Verifique os logs para mais detalhes."
  };

  return messages[errorCode] || "Erro desconhecido durante a autenticação.";
}

/**
 *
 */
export default function OAuth2ErrorPage() {
  const [searchParameters] = useSearchParams();
  const [autoCloseSeconds, setAutoCloseSeconds] = useState(TRINTA);

  const error = searchParameters.get("error") || "unknown_error";
  const errorDescription = searchParameters.get("error_description") || "Erro desconhecido na autenticação OAuth2";

  useEffect(() => {
    // Get return URL from sessionStorage
    const flowStateStored = sessionStorage.getItem("oauth2_flow_state");
    let returnUrl = "/email-accounts"; // Default fallback

    if (flowStateStored) {
      try {
        const flowState = JSON.parse(flowStateStored) as { returnUrl?: string };
        if (flowState.returnUrl) {
          returnUrl = flowState.returnUrl;
        }
      } catch (error_) {
        console.error("Failed to parse flow state:", error_);
      }
    }

    // Auto-redirect countdown
    const interval = setInterval(() => {
      setAutoCloseSeconds((previous) => {
        if (previous <= 1) {
          clearInterval(interval);
          // Redirect back to original page
          window.location.href = returnUrl;
          return 0;
        }
        return previous - 1;
      });
    }, MIL);

    return () => clearInterval(interval);
  }, [error, errorDescription]);

  return (
    <Container size="sm" py="xl">
      <Stack gap="lg" align="center">
        <IconAlertTriangle size={64} color="orange" />

        <Title order={2}>Falha na autenticação</Title>

        <Alert color="red" title="Erro OAuth2" w="100%">
          <Stack gap="sm">
            <Text size="sm">{getErrorMessage(error)}</Text>
            {errorDescription && errorDescription !== "Erro desconhecido na autenticação OAuth2" && (
              <Text size="sm" c="dimmed" fs="italic">
                {errorDescription}
              </Text>
            )}

            <Stack gap="xs">
              <Text size="xs" fw={500}>
                Detalhes técnicos:
              </Text>
              <Code block>{errorDescription}</Code>
              <Text size="xs" c="dimmed">
                Código do erro: <Code>{error}</Code>
              </Text>
            </Stack>
          </Stack>
        </Alert>

        <Stack gap="xs" align="center">
          <Text size="sm" c="dimmed">
            Redirecionando automaticamente em {autoCloseSeconds} segundo
            {autoCloseSeconds === 1 ? "" : "s"}...
          </Text>
          <Button size="sm" variant="subtle" onClick={handleRedirectBack}>
            Voltar agora
          </Button>
        </Stack>
      </Stack>
    </Container>
  );
}
